/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IActionContext } from '@msdyn365-commerce/core';
import { BypassCache, IDataServiceRequest } from '@msdyn365-commerce/retail-proxy';
import { executeAction } from '@msdyn365-commerce/retail-proxy/dist/retail-action';

/**
 * Kill switch for the impersonation header suppression. The fix is active in every environment unless this
 * switch is set to true, which restores the previous behavior without a redeploy.
 */
export const OMIT_IMPERSONATION_HEADERS_KILL_SWITCH = 'b2bOmitImpersonationHeaders_KillSwitch';

const omitImpersonationHeaders = (context: IActionContext): IActionContext => ({
    ...context,
    requestContext: {
        ...context.requestContext,
        user: { ...context.requestContext.user, isB2bContact: false, isOBORequest: false }
    }
});

/**
 * Executes a request as the signed-in user instead of the customer that user acts on behalf of.
 *
 * The retail proxy derives the `CAN` / `CPI` headers from `isB2bContact` and `isOBORequest`, which makes
 * Commerce Scale Unit evaluate the request against the impersonated contact. That is wrong for the APIs that
 * report what the signed-in user may reach, so those flags are cleared on a copy of the context.
 *
 * `executeAction` is bypassed because its request cache keys responses by operation only, which would let an
 * un-impersonated response leak to callers that do need the impersonated context. Turning the kill switch on
 * restores the original path through `executeAction`, exactly like the generated `*Async` helpers.
 *
 * `isOBORequest` is also read by the proxy error parser, so a 401 on these requests reloads the page to
 * re-authenticate instead of being swallowed. That only happens on an expired token and is bounded by the
 * parser's own redirect counter.
 * @param request - Request created by a generated `create*Input` helper.
 * @param context - The action context of the caller.
 * @param bypassCache - Cache behaviour applied when the kill switch is on.
 * @returns The result of the request.
 */
export async function executeWithoutImpersonation<TResult>(
    request: IDataServiceRequest,
    context: IActionContext,
    bypassCache?: BypassCache
): Promise<TResult> {
    const { [OMIT_IMPERSONATION_HEADERS_KILL_SWITCH]: isKillSwitchOn } = context.requestContext?.features ?? {};

    if (isKillSwitchOn === true) {
        return executeAction<TResult>(request, { callerContext: context, bypassCache });
    }

    return request.execute<TResult>(omitImpersonationHeaders(context));
}
